import { DownwardOutlineIcon, UpwardOutlineIcon } from '@carboncopyco/icons';
import { Button } from '@carboncopyco/ui';
import { useEffect, useState } from 'react';
import ElectronMonitoring from './ElectronMonitoring';
import ReactMonitoring from './ReactMonitoring';
import { AnalyticsMonitoring } from './analytics/AnalyticsMonitoring';

const MONITORING_SIZE_KEY = 'monitoring-size';
const MONITORING_MINIMIZED_KEY = 'monitoring-minimized';
const ACTIVE_TAB_KEY = 'monitoring-active-tab';

type LocalStorageSizeType = 'sm' | 'lg' | undefined;
type MonitorTabType = 'electron' | 'react' | 'analytics';

const Monitoring = () => {
  const [activeTab, setActiveTab] = useState<MonitorTabType>('electron');
  const [size, setSize] = useState<'sm' | 'lg'>('sm');
  const [minimized, setMinimized] = useState(false);

  const handleSetActiveTab = (tab: MonitorTabType) => {
    setActiveTab(tab);
    localStorage.setItem(ACTIVE_TAB_KEY, tab);
  };

  const handleResize = () => {
    const newSize = size === 'sm' ? 'lg' : 'sm';
    setSize(newSize);
    localStorage.setItem(MONITORING_SIZE_KEY, newSize);
  };

  const handleMinimize = (minimize: boolean) => {
    setMinimized(minimize);
    localStorage.setItem(MONITORING_MINIMIZED_KEY, minimize.toString());
  };

  useEffect(() => {
    const storageActiveTab = localStorage.getItem(
      ACTIVE_TAB_KEY
    ) as MonitorTabType;
    if (!storageActiveTab) {
      localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
      return;
    }
    setActiveTab(storageActiveTab);
  }, []);

  useEffect(() => {
    const storageSize = localStorage.getItem(
      MONITORING_SIZE_KEY
    ) as LocalStorageSizeType;
    if (!storageSize) {
      localStorage.setItem(MONITORING_SIZE_KEY, size);
      return;
    }
    setSize(storageSize);
  }, []);

  useEffect(() => {
    const storageMinimized = localStorage.getItem(MONITORING_MINIMIZED_KEY);
    if (!storageMinimized) {
      localStorage.setItem(MONITORING_MINIMIZED_KEY, minimized.toString());
      return;
    }
    setMinimized(storageMinimized === 'true');
  }, []);

  return minimized ? (
    <div className="fixed bottom-10 right-10 z-[2000]">
      <Button
        secondary
        onClick={() => handleMinimize(false)}
        label="Monitoring"
      />
    </div>
  ) : (
    <div
      style={{ height: size === 'sm' ? '250px' : '500px' }}
      className="px-4 py-6 overflow-auto border-t-4 relative z-[2000] bg-white"
    >
      <div className="flex gap-x-2 mb-6">
        <Button
          xSmall
          onClick={() => handleSetActiveTab('electron')}
          secondary={activeTab !== 'electron'}
          label="Electron"
        />
        <Button
          xSmall
          onClick={() => handleSetActiveTab('react')}
          secondary={activeTab !== 'react'}
          label="React"
        />
        <Button
          xSmall
          onClick={() => handleSetActiveTab('analytics')}
          secondary={activeTab !== 'analytics'}
          label="Analytics"
        />
        <div className="ml-auto flex gap-x-2">
          <Button
            xSmall
            onClick={handleResize}
            secondary
            icon={
              <div className="flex items-center justify-center w-6">
                {size === 'sm' ? (
                  <UpwardOutlineIcon />
                ) : (
                  <DownwardOutlineIcon />
                )}
              </div>
            }
          />
          <Button
            xSmall
            onClick={() => handleMinimize(true)}
            secondary
            icon={<div className="flex items-center justify-center w-6">-</div>}
          />
        </div>
      </div>

      {activeTab === 'electron' && <ElectronMonitoring />}
      {activeTab === 'react' && <ReactMonitoring />}
      {activeTab === 'analytics' && <AnalyticsMonitoring />}
    </div>
  );
};

export default Monitoring;
